import { useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import {
  activeChild,
  cycleProgress,
  currentPlan,
  goalEvidence,
  planGoals,
  planPriorities,
  planPrinciples,
  planTitle,
  planWeeks,
  type PlanWeek,
} from '../../store/selectors'
import { todayISO } from '../../lib/dates'
import { WeekView } from './WeekView'
import { CycleReview } from './CycleReview'
import { EmptyState } from '../ui/EmptyState'

/**
 * The whole arc, on one screen.
 *
 * Six weeks is long enough that a parent loses the thread of why they are doing
 * any of it. This exists to answer that in one look: what we are going for,
 * what we decided to leave alone, and where in the run we are.
 *
 * Progress is deliberately counted in weeks elapsed and moments captured, never
 * as a percentage of a goal. A goal is not a thing you are 60% of the way
 * through, and a number that looks like a score would be read as one.
 */
export function CycleOverview() {
  const child = useAuroraStore(activeChild)
  const plan = useAuroraStore(currentPlan)
  const title = useAuroraStore(planTitle)
  const weeks = useAuroraStore(planWeeks) as PlanWeek[]
  const goals = useAuroraStore(goalEvidence)
  const priorities = useAuroraStore(planPriorities)
  const principles = useAuroraStore(planPrinciples)

  const today = todayISO((child as any)?.timezone)
  const progress = useAuroraStore((s: any) => cycleProgress(s, today))

  const [openWeek, setOpenWeek] = useState<number | null>(null)
  const [reviewing, setReviewing] = useState(false)

  const ask = (text: string) => {
    const s = auroraStore.getState() as any
    s.setComposing(text)
    s.setView('thread')
  }

  if (!plan) {
    return (
      <EmptyState
        title="No plan yet"
        body={`Ask Aurora for one. It will build the whole arc around what it already knows about ${(child as any)?.name ?? 'her'} — and you can change any of it.`}
        action={{
          label: 'Ask for a plan',
          onClick: () => ask(`Make a plan for ${(child as any)?.name ?? 'her'}`),
        }}
      />
    )
  }

  if (reviewing) return <CycleReview onBack={() => setReviewing(false)} />

  const week = weeks.find((w) => w.number === openWeek)
  if (week) return <WeekView week={week} onBack={() => setOpenWeek(null)} />

  const lastDay = weeks.flatMap((w) => w.days).map((d) => d.date).filter(Boolean).sort().pop()
  const finished = Boolean(lastDay && lastDay < today)

  return (
    <div className="cycle">
      <header className="cycle-head">
        <h3 className="cycle-title">{title ?? (plan as any).title}</h3>
        <p className="cycle-sub tnum">
          {progress.weeks > 0
            ? `Week ${Math.max(1, progress.weeksElapsed)} of ${progress.weeks}`
            : 'Not laid out yet'}
          {progress.blocks > 0 && ` · ${progress.blocksSettled} of ${progress.blocks} activities behind you`}
        </p>
      </header>

      {finished && (
        <button className="cycle-ceremony" onClick={() => setReviewing(true)}>
          <span className="cycle-ceremony-glow" aria-hidden="true" />
          <span className="cycle-ceremony-text">
            <span className="cycle-ceremony-title">This cycle is done</span>
            <span className="cycle-ceremony-sub">See what moved, and what comes next</span>
          </span>
          <ArrowIcon />
        </button>
      )}

      {goals.length > 0 && (
        <section className="cycle-section">
          <h4 className="section-title">What we are going for</h4>
          <ul className="cycle-goals">
            {goals.map(({ goal, moments }, i) => (
              <li
                key={i}
                className="cycle-goal"
                style={
                  {
                    '--domain-color': goal.domain
                      ? `var(--domain-${goal.domain})`
                      : 'var(--line-strong)',
                  } as React.CSSProperties
                }
              >
                <span className="cycle-goal-statement">{goal.statement}</span>
                <span className="cycle-goal-evidence tnum">
                  {moments === 0
                    ? 'nothing captured yet'
                    : `${moments} ${moments === 1 ? 'moment' : 'moments'} so far`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {priorities.length > 0 && (
        <section className="cycle-section">
          <h4 className="section-title">In this order</h4>
          <ol className="cycle-priorities">
            {priorities.map((p, i) => (
              <li
                key={p}
                style={{ '--domain-color': `var(--domain-${p})` } as React.CSSProperties}
              >
                <span className="cycle-priority-rank tnum">{i + 1}</span>
                {p}
              </li>
            ))}
          </ol>
          <p className="cycle-note">
            Everything else keeps ticking over. A cycle that chases all thirteen areas moves none
            of them.
          </p>
        </section>
      )}

      <section className="cycle-section">
        <div className="section-head">
          <h4 className="section-title">The weeks</h4>
          <button className="btn btn-quiet btn-sm" onClick={() => ask('How is this cycle going?')}>
            Ask how it is going
          </button>
        </div>

        {weeks.length === 0 ? (
          <p className="cycle-note">
            The weeks are not laid out yet. Ask Aurora to build them when you are ready.
          </p>
        ) : (
          <ol className="cycle-weeks">
            {weeks.map((w) => {
              const blocks = w.days.flatMap((d) => d.blocks)
              const settled = blocks.filter((b) => b.status && b.status !== 'pending').length
              const live = w.days.some((d) => d.date === today)
              return (
                <li key={w.number}>
                  <button
                    className={`cycle-week${live ? ' is-live' : ''}`}
                    onClick={() => setOpenWeek(w.number)}
                    aria-current={live ? 'step' : undefined}
                  >
                    <span className="cycle-week-n tnum">{w.number}</span>
                    <span className="cycle-week-text">
                      <span className="cycle-week-theme">{w.theme ?? 'Untitled week'}</span>
                      <span className="cycle-week-meta tnum">
                        {blocks.length
                          ? `${settled} of ${blocks.length} done`
                          : 'nothing planned yet'}
                      </span>
                    </span>
                    <span className="cycle-week-dots" aria-hidden="true">
                      {blocks.slice(0, 12).map((b, i) => (
                        <span
                          key={i}
                          className={`cycle-dot${b.status && b.status !== 'pending' ? ' is-on' : ''}`}
                        />
                      ))}
                    </span>
                  </button>
                </li>
              )
            })}
          </ol>
        )}
      </section>

      {principles.length > 0 && (
        <section className="cycle-section">
          <h4 className="section-title">Written under</h4>
          <ul className="cycle-principles">
            {principles.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </section>
      )}

      {!finished && weeks.length > 0 && (
        <div className="cycle-foot">
          <button className="btn btn-quiet btn-sm" onClick={() => setReviewing(true)}>
            Look at the cycle review
          </button>
        </div>
      )}
    </div>
  )
}

const ArrowIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h13M12 5l7 7-7 7" />
  </svg>
)
